import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import { AgentInlineSelect } from './ai-agent-ui';
import type { AgentMessageResponse } from '../types/agent';
import { environmentPlaceholderSx } from '../utils/environmentUtils';
import { dispatchEnvironmentVariablesChanged } from '../utils/environmentVariablesEvents';
import { isAgentFailureMessage } from '../utils/agentChatMessageStyle';

export type AgentWidgetEnvVariableFormProps = {
  chatId?: number | null;
  defaultProjectId?: number | '';
  defaultProjectName?: string;
  defaultEnvironmentId?: number | '';
  defaultKey?: string;
  defaultValue?: string;
  defaultDescription?: string;
  onClose: () => void;
  onUserMessage: (text: string) => void;
  onAgentReply: (res: AgentMessageResponse) => void | Promise<void>;
  onChatIdChange?: (chatId: number) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const AgentWidgetEnvVariableForm: React.FC<AgentWidgetEnvVariableFormProps> = ({
  chatId,
  defaultProjectId = '',
  defaultProjectName = '',
  defaultEnvironmentId = '',
  defaultKey = '',
  defaultValue = '',
  defaultDescription = '',
  onClose,
  onUserMessage,
  onAgentReply,
  onChatIdChange,
  onLoadingChange,
}) => {
  const { refreshProjects, setSelectedProject } = useProjects();
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [availableEnvironments, setAvailableEnvironments] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingEnvironments, setLoadingEnvironments] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<number | ''>('');
  const [varKey, setVarKey] = useState(defaultKey);
  const [varValue, setVarValue] = useState(defaultValue);
  const [varDescription, setVarDescription] = useState(defaultDescription);
  const [submitting, setSubmitting] = useState(false);

  const mergeProjectOption = useCallback(
    (list: Array<{ id: number; name: string }>) => {
      if (defaultProjectId === '' || Number.isNaN(Number(defaultProjectId))) {
        return list;
      }
      const id = Number(defaultProjectId);
      if (list.some((p) => p.id === id)) {
        return list;
      }
      const name = defaultProjectName.trim() || `Project ${id}`;
      return [...list, { id, name }].sort((a, b) => a.name.localeCompare(b.name));
    },
    [defaultProjectId, defaultProjectName],
  );

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      await refreshProjects();
      const fetched = await apiService.getProjects();
      const mapped = fetched.map((p) => ({ id: p.id, name: p.name }));
      setAvailableProjects(mergeProjectOption(mapped));
    } catch {
      if (defaultProjectId !== '' && !Number.isNaN(Number(defaultProjectId))) {
        setAvailableProjects(
          mergeProjectOption([
            {
              id: Number(defaultProjectId),
              name: defaultProjectName.trim() || `Project ${defaultProjectId}`,
            },
          ]),
        );
      } else {
        setAvailableProjects([]);
      }
    } finally {
      setLoadingProjects(false);
    }
  }, [defaultProjectId, defaultProjectName, mergeProjectOption, refreshProjects]);

  const loadEnvironments = useCallback(async (projectId: number | '') => {
    if (projectId === '') {
      setAvailableEnvironments([]);
      setSelectedEnvironmentId('');
      return;
    }
    setLoadingEnvironments(true);
    try {
      const envs = await apiService.getEnvironmentsByProject(Number(projectId));
      const mapped = envs.map((e) => ({ id: e.id, name: e.name }));
      setAvailableEnvironments(mapped);
      setSelectedEnvironmentId((prev) => {
        if (prev !== '' && mapped.some((e) => e.id === prev)) {
          return prev;
        }
        if (
          defaultEnvironmentId !== '' &&
          mapped.some((e) => e.id === defaultEnvironmentId)
        ) {
          return defaultEnvironmentId;
        }
        return mapped[0]?.id ?? '';
      });
    } catch {
      setAvailableEnvironments([]);
      setSelectedEnvironmentId('');
    } finally {
      setLoadingEnvironments(false);
    }
  }, [defaultEnvironmentId]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (loadingProjects) return;
    if (defaultProjectId !== '' && availableProjects.some((p) => p.id === defaultProjectId)) {
      setSelectedProjectId(defaultProjectId);
      return;
    }
    if (availableProjects.length === 1) {
      setSelectedProjectId(availableProjects[0].id);
    }
  }, [defaultProjectId, availableProjects, loadingProjects]);

  useEffect(() => {
    if (selectedProjectId === '') {
      setAvailableEnvironments([]);
      setSelectedEnvironmentId('');
      return;
    }
    void loadEnvironments(selectedProjectId);
  }, [selectedProjectId, loadEnvironments]);

  const projectSelectValue = useMemo(() => {
    if (selectedProjectId === '') return '';
    return availableProjects.some((p) => p.id === selectedProjectId) ? selectedProjectId : '';
  }, [availableProjects, selectedProjectId]);

  const environmentSelectValue = useMemo(() => {
    if (selectedEnvironmentId === '') return '';
    return availableEnvironments.some((e) => e.id === selectedEnvironmentId)
      ? selectedEnvironmentId
      : '';
  }, [availableEnvironments, selectedEnvironmentId]);

  const handleSave = async () => {
    const key = varKey.trim();
    const description = varDescription.trim();
    if (!selectedEnvironmentId || !key) return;

    const envName =
      availableEnvironments.find((e) => e.id === selectedEnvironmentId)?.name ??
      `environment #${selectedEnvironmentId}`;
    const userText = `Add environment variable "${key}" in environment "${envName}".`;
    onUserMessage(userText);
    setSubmitting(true);
    onLoadingChange?.(true);
    try {
      const res = await apiService.agentMessage({
        action: 'add_environment_variable',
        chatId: chatId ?? undefined,
        payload: {
          projectId: selectedProjectId || undefined,
          environmentId: selectedEnvironmentId,
          key,
          value: varValue,
          ...(description ? { description } : {}),
        },
      });
      if (res.state?.chatId != null) {
        onChatIdChange?.(res.state.chatId as number);
      }
      if (res.state?.projectId != null) {
        try {
          const list = await apiService.getProjects();
          const match = list.find((p) => p.id === res.state!.projectId);
          if (match) setSelectedProject(match);
        } catch {
          /* ignore */
        }
      }
      const failed = Boolean(res.error) || isAgentFailureMessage(res.reply || '', res.actions);
      if (!failed) {
        dispatchEnvironmentVariablesChanged({
          environmentId: Number(selectedEnvironmentId),
          projectId: selectedProjectId === '' ? undefined : Number(selectedProjectId),
          source: 'widget',
        });
      }
      await onAgentReply(res);
      if (!failed) {
        onClose();
      }
    } finally {
      setSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const busy = submitting || loadingProjects || loadingEnvironments;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 0.5,
        p: 1.25,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(15,23,42,0.12)',
        ...environmentPlaceholderSx,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Add environment variable
      </Typography>

      <Box sx={{ mb: 1 }}>
        <AgentInlineSelect
          id="widget-agent-env-var-project"
          label="Project"
          fullWidth
          value={projectSelectValue === '' ? '' : projectSelectValue}
          disabled={busy}
          emptyLabel={loadingProjects ? 'Loading projects…' : 'No projects found'}
          options={availableProjects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedProjectId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <AgentInlineSelect
          id="widget-agent-env-var-env"
          label="Environment"
          fullWidth
          value={environmentSelectValue === '' ? '' : environmentSelectValue}
          disabled={busy || !selectedProjectId}
          emptyLabel={
            loadingEnvironments
              ? 'Loading environments…'
              : selectedProjectId
                ? 'No environments found'
                : 'Select a project first'
          }
          options={availableEnvironments.map((env) => ({
            value: env.id,
            label: env.name,
          }))}
          onChange={(raw) => {
            const parsed = Number(raw);
            setSelectedEnvironmentId(Number.isNaN(parsed) || raw === '' ? '' : parsed);
          }}
        />
      </Box>

      <TextField
        label="Key"
        size="small"
        fullWidth
        value={varKey}
        onChange={(e) => setVarKey(e.target.value)}
        disabled={busy}
        autoComplete="off"
        sx={{ mb: 1 }}
      />
      <TextField
        label="Value"
        size="small"
        fullWidth
        value={varValue}
        onChange={(e) => setVarValue(e.target.value)}
        disabled={busy}
        autoComplete="off"
        sx={{ mb: 1 }}
      />
      <TextField
        label="Description"
        size="small"
        fullWidth
        value={varDescription}
        onChange={(e) => setVarDescription(e.target.value)}
        disabled={busy}
        autoComplete="off"
        sx={{ mb: 1 }}
      />

      {!loadingProjects && availableProjects.length === 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          Create a project first, then add an environment variable.
        </Typography>
      )}
      {selectedProjectId && !loadingEnvironments && availableEnvironments.length === 0 && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
          No environments found for this project. Create an environment first.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleSave()}
          disabled={busy || !selectedEnvironmentId || !varKey.trim()}
          sx={{ textTransform: 'none' }}
        >
          {busy ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </Box>

      <Divider sx={{ mt: 1.25 }} />
    </Box>
  );
};

export default AgentWidgetEnvVariableForm;
